import * as XLSX from 'xlsx';

/**
 * Reads the sheets of an uploaded workbook. Kept free of Angular and app imports because
 * the file-parser web worker compiles it under its own, narrower tsconfig.
 */

/** Why a sheet cannot be uploaded as a table. */
export type WorkbookSheetProblem = 'missing header' | 'no data';

/** One worksheet as the parser hands it over: rows as arrays, row 0 is the header row. */
export interface WorkbookSheet {
    name: string;
    data: unknown[][];
    error: WorkbookSheetProblem | null;
}

/** Shrink `!ref` to the last row/column that actually contains cell data. */
export function trimWorksheetToDataExtent(worksheet: XLSX.WorkSheet): XLSX.WorkSheet {
    const ref = worksheet['!ref'];
    if (!ref) {
        return worksheet;
    }

    const declared = XLSX.utils.decode_range(ref);
    let maxRow = declared.s.r;
    let maxCol = declared.s.c;

    for (const key of Object.keys(worksheet)) {
        if (key.charCodeAt(0) === 33) {
            continue;
        }
        const cell = XLSX.utils.decode_cell(key);
        if (cell.r > maxRow) {
            maxRow = cell.r;
        }
        if (cell.c > maxCol) {
            maxCol = cell.c;
        }
    }

    const trimmedRef = XLSX.utils.encode_range({
        s: { r: declared.s.r, c: declared.s.c },
        e: { r: maxRow, c: maxCol },
    });

    if (trimmedRef === ref) {
        return worksheet;
    }

    return { ...worksheet, '!ref': trimmedRef };
}

/** Reads one worksheet into rows and flags the two shapes that cannot become a table. */
export function readWorksheet(name: string, worksheet: XLSX.WorkSheet | undefined): WorkbookSheet {
    if (!worksheet) {
        return { name, data: [], error: 'no data' };
    }
    // raw: false keeps cell values as formatted strings, so description text like
    // "20 S PUFF PASTRY (9X800G)" is not turned into the number 20.
    const data: unknown[][] = XLSX.utils.sheet_to_json(trimWorksheetToDataExtent(worksheet), {
        header: 1,
        raw: false,
    });
    if (!data.length || !Array.isArray(data[0])) {
        return { name, data: [], error: 'no data' };
    }
    const headerRow = data[0] as unknown[];
    if (headerRow.length > headerRow.filter(ele => ele).length) {
        return { name, data, error: 'missing header' };
    }
    return { name, data, error: null };
}

/** Every sheet of the workbook, in workbook order. */
export function readWorkbookSheets(workbook: XLSX.WorkBook): WorkbookSheet[] {
    return workbook.SheetNames.map(name => readWorksheet(name, workbook.Sheets[name]));
}
